import type { Card, QueryExpression } from "@amzn/ads-blink-client";
import { Providers } from "@amzn/ads-blink-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { camelCase, capitalize, cloneDeep, startCase } from "lodash";

import { InsightSearchReplacements } from "../config/filters/unified";
import { ALL_FAMILIES, allPackagedInsightScope } from "../constants/filters";
import {
    InsightFamilyMetadataAttributes,
    InsightFamilyMetadataBrandType,
} from "../hooks/insightFamilies/useInsightFamiliesFiltersAction";
import { FeatureFlagsStore } from "../store/featureFlags";
import { discoveryFilterStore } from "../store/filters";
import { unifiedFilterStore } from "../store/unifiedFilters";
import type { CardDetail } from "../types/card";
import { CardDetailsSource } from "../types/card";
import type { Item } from "../types/common";
import type {
    AllFilterOptionId,
    IBaseFilterOptionModel,
    UnifiedFilters,
} from "../types/filters/common";
import type { AllUnifiedFilterKeys } from "../types/filters/unified";
import { insightTypeLabelToValue } from "../types/filters/unified";
import { UnifiedFilterField } from "../types/filters/unified";
import { InsightFamilyName } from "../types/providers";

import {
    formatFamilyName,
    formatObjectives,
    formatStartEndDate,
    formatUploadDate,
} from "./insightFamilies.utils";

export const scopeToProviderMap: Record<string, Providers[]> = {
    // CAIRE/Package API:
    CAIRE: [Providers.PACKAGE],
    // CBD Custom Insights
    "Ad Products": [Providers.TEMPLATES],
    "Advertiser Goals": [Providers.TEMPLATES],
    "Downstream Impact": [Providers.TEMPLATES],
    "Industry Insights": [Providers.TEMPLATES],
    "Seasonal Events": [Providers.TEMPLATES],
    // Standalone Insights:
    [InsightFamilyName.AUDIENCE_FUNNEL_SIZING]: [Providers.INSIGHT],
    [InsightFamilyName.BRAND_LOYALTY]: [Providers.INSIGHT],
    [InsightFamilyName.BRANDED_SEARCH_TRENDS]: [Providers.INSIGHT],
    [InsightFamilyName.NEW_TO_BRAND]: [Providers.INSIGHT],
    [InsightFamilyName.PATH_TO_PURCHASE]: [Providers.INSIGHT],
    [InsightFamilyName.PEER_BENCHMARKING]: [Providers.INSIGHT],
    [InsightFamilyName.PEER_BENCHMARKING_UAT]: [Providers.INSIGHT],
    [InsightFamilyName.BRAND_LOYALTY_UAT]: [Providers.INSIGHT],
    [InsightFamilyName.REPEAT_PURCHASER]: [Providers.INSIGHT],
    [InsightFamilyName.SHARE_OF_VOICE]: [Providers.INSIGHT],
    [InsightFamilyName.INSIGHT_GENIE]: [Providers.INSIGHT],
    // Marketing Narratives. TODO: CHECK
    "18d0e2d6-bbf9-4346-b6ea-6a21a977b52f": [Providers.MARKETING_NARRATIVE],
    "efb3438d-cb13-4695-9774-16cbed61f0b4": [Providers.MARKETING_NARRATIVE],
    "952d9654-2a8d-4a5a-a1ac-c66d3ec84f20": [Providers.MARKETING_NARRATIVE],
    "7dd4cf1d-825d-4090-91b5-196c4bbc4c34": [Providers.MARKETING_NARRATIVE],
    "31f2eb16-69bc-49f4-9118-963c4ab0f1da": [Providers.MARKETING_NARRATIVE],
    "18fc2134-0df1-4566-ad35-68b318e2ef22": [Providers.MARKETING_NARRATIVE],
    "e81f2681-41a7-4393-a2ed-fa7d56bc7b03": [Providers.MARKETING_NARRATIVE],
    "f9e4326e-e57b-4711-99af-5d5d8d74741f": [Providers.MARKETING_NARRATIVE],
    "b0e50e0e-afb8-4fef-a4d3-39b639503bfe": [Providers.MARKETING_NARRATIVE],
    // All options
    [allPackagedInsightScope.value!]: [Providers.PACKAGE, Providers.TEMPLATES],
    [ALL_FAMILIES]: [Providers.INSIGHT],
};

export const getCurrentProviders = (
    isTypeDropdown: boolean = false,
): Providers[] => {
    if (FeatureFlagsStore.getState().unifiedSearch) {
        if (isTypeDropdown) {
            return Object.values(Providers);
        }

        const selectedTypeFilter = unifiedFilterStore
            .getState()
            .filters.UNIFIED_TYPE.filter(
                (filter) => filter.isSelected && !filter.isDisabled,
            );

        return [...new Set(selectedTypeFilter.flatMap((type) => type.scopes))];
    }

    const state = discoveryFilterStore.getState();
    const subType = state.getContentSubType().scope;

    const providers = new Set<Providers>([
        Providers.PACKAGE,
        Providers.TEMPLATES,
        Providers.INSIGHT,
        Providers.MARKETING_NARRATIVE,
    ]);
    if (subType && scopeToProviderMap[subType]) {
        providers.clear();
        scopeToProviderMap[subType].forEach((provider) =>
            providers.add(provider),
        );
    }
    return [...providers];
};

// Due to provider specific nuances, there are certain scopes we want to not include when constructing the unified
// search query. They are stored here for safe-keeping.
const typesToIgnoreScopeFor: string[] = [
    ALL_FAMILIES,
    allPackagedInsightScope.value!,
    "CAIRE",
    "Pitched Content",
];

/**
 * Converts the current filter state into the queryExpression needed to send to the IStore backend
 * @returns The QueryExpression needed for the IStore APIs
 */
export const getUnifiedSearchQueryExpression = (
    ignoreField?: UnifiedFilterField,
    ignoreScope?: boolean,
): QueryExpression => {
    const state = discoveryFilterStore.getState();
    const [subType, searchQuery, filters, filterOptions] = [
        state.getContentSubType(),
        state.getInsightsFilters().UNIFIED.searchQuery,
        { ...state.getInsightsFilters().UNIFIED.fieldSelections },
        { ...state.getInsightsFilters().UNIFIED.filterOptions },
    ];
    const searchFilterInversion =
        FeatureFlagsStore.getState().searchFilterInversion;

    // Optionally ignore certain filters that are applied
    if (ignoreField) {
        filters[ignoreField] = [];
    }

    // Check for two query optimizations:
    // 1. If the user has select all options, send nothing since that equivalent in OpenSearch
    // 2. Check if it would be less "operations" to send !== to what wasn't selected for each filter
    const shouldInvert: Record<string, boolean> = {};
    Object.keys(filters).map((field) => {
        const selectedLength = filters[field]?.length ?? 0;
        const optionsLength = filterOptions[field]?.length ?? 0;
        const unselectedLength = optionsLength - selectedLength;

        // If all options are selected, send nothing
        // Otherwise check if we need to invert our selections
        if (selectedLength === optionsLength) {
            filters[field] = [];
        } else if (
            searchFilterInversion &&
            optionsLength > 0 &&
            unselectedLength < selectedLength
        ) {
            shouldInvert[field] = true;
            const labels = filters[field].map((item: Item) => item.label);
            filters[field] = filterOptions[field].filter(
                (item: Item) => !labels.includes(item.label),
            );
        }
    });

    // Create a mapping from the name Cerebro expects (the key), to our fields
    const exactMappings: Record<
        string,
        { values: (string | number)[]; invert: boolean }
    > = {
        browseNodeName: {
            values: filters.UNIFIED_BROWSENODENAME.map((i) => i.label),
            invert: shouldInvert[UnifiedFilterField.BROWSE_NODE],
        },
        language: {
            values: filters.UNIFIED_LANGUAGE.map((i) => i.label),
            invert: shouldInvert[UnifiedFilterField.LANGUAGE],
        },
        topic: {
            values: filters.UNIFIED_TOPIC.map((i) => i.label),
            invert: shouldInvert[UnifiedFilterField.TOPIC],
        },
    };

    // Create a mapping from the name Cerebro expects (the key), to our fields
    const fuzzyMappings: Record<
        string,
        { values: (string | number)[]; invert: boolean }
    > = {
        objectives: {
            values: filters.UNIFIED_OBJECTIVES.map((i) => i.label),
            invert: shouldInvert[UnifiedFilterField.OBJECTIVE],
        },
        brandName: {
            values: filters.UNIFIED_BRANDNAME.map((i) => i.label),
            invert: shouldInvert[UnifiedFilterField.BRAND],
        },
    };

    // Only add type if subType.scope exists and is not "All"
    if (
        subType.scope &&
        !ignoreScope &&
        !typesToIgnoreScopeFor.includes(subType.scope)
    ) {
        exactMappings.type = {
            values: [subType.scope],
            invert: false,
        };
    }

    // Remove ( and ) from brand name so we don't break the parser
    fuzzyMappings.brandName.values = fuzzyMappings.brandName.values.map((b) =>
        b.toString().replace(/[()]/g, " "),
    );

    // Build our expressions to send to the Cerebro. This just puts it in the format:
    // (field1 == "value1" || field1 == "value2") && (field2 == "value3")
    const [exactExpression, fuzzyExpression] = [
        exactMappings,
        fuzzyMappings,
    ].map((mappings) =>
        Object.entries(mappings)
            .map(([name, { values, invert }]) => {
                const comparison = invert ? "!=" : "==";
                const joinOperator = invert ? " && " : " || ";
                return values
                    .map((v) => `${name} ${comparison} "${v}"`)
                    .join(joinOperator);
            })
            .filter((o) => o)
            .map((e) => `(${e})`)
            .join(" && "),
    );

    return {
        searchQuery,
        exactExpression,
        fuzzyExpression,
        operator: "AND",
    };
};

export const getSingleIdentifierQueryExpression = (
    identifer: string,
): QueryExpression => {
    return {
        searchQuery: "",
        exactExpression: `id=="${identifer}"`,
        fuzzyExpression: "",
        operator: "AND",
    };
};

/**
 * These are the characters that will break the Cerebro OpenSearch parser. We need to make sure these are removed from
 * any values we send to it
 */
const deniedLetters = "=()";

/**
 * Helper RegEx so we don't have to redclare it constantly
 */
const deniedCharsRegExp = new RegExp(`[${deniedLetters}]`, "g");

/**
 * Remove any denied characters from the list of values so that Cerebro can parse them properly
 * @param values The values to process
 */
const processDeniedChars = (values: string[]) => {
    const containsDenied = values.some(
        (value) =>
            typeof value === "string" &&
            [...deniedLetters].some((char) => value.includes(char)),
    );

    if (containsDenied) {
        // Replace denied characters with space
        return values.map((value) =>
            typeof value === "string"
                ? value.replace(deniedCharsRegExp, " ")
                : value,
        );
    }

    return values;
};

/**
 * Converts the current filter state into the queryExpression needed to send to the IStore backend
 * @returns The QueryExpression needed for the IStore APIs
 */
export const getUnifiedSearchQueryExpressionV2 = (
    ignoreField?: AllUnifiedFilterKeys,
    ignoreScope?: boolean,
): QueryExpression => {
    const state = unifiedFilterStore.getState();
    const [searchQuery, clonedfilters] = [
        cloneDeep(state.searchQuery),
        cloneDeep(state.filters),
    ];

    // Filter down to only the values that are selected
    const filters = Object.entries(clonedfilters).reduce(
        (acc, [key, value]) => {
            acc[key] = (value as { isSelected: boolean }[]).filter(
                (item) => item.isSelected,
            );
            return acc;
        },
        {} as UnifiedFilters,
    );
    // Optionally ignore certain filters that are applied
    if (ignoreField) {
        filters[ignoreField] = [];
    }

    // Create a mapping from the name Cerebro expects (the key), to our fields
    // The keys are defined here - https://code.amazon.com/packages/AdsCerebroSharedModel/blobs/mainline/--/model/common/MetadataList.smithy
    // Future state we will want to reference them directly
    const [exactMappings, fuzzyMappings] = Object.entries({
        browseNodeName: filters.UNIFIED_BROWSENODENAME,
        language: filters.UNIFIED_LANGUAGE,
        topic: filters.UNIFIED_TOPIC,
        packageName: filters.UNIFIED_PACKAGENAME,
        entityName: filters.UNIFIED_ENTITYNAME,
        adActivationChannel: filters.UNIFIED_ADACTIVATIONCHANNEL,
        advertiserGoal: filters.UNIFIED_ADVERTISERGOAL,
        fileType: filters.UNIFIED_FILETYPE,
        marketplace: filters.UNIFIED_MARKETPLACE,
        marketingStrategy: filters.UNIFIED_MARKETINGSTRATEGY,
        seasonalEvents: filters.UNIFIED_SEASONALEVENTS,
        subVertical: filters.UNIFIED_SUBVERTICAL,
        vertical: filters.UNIFIED_VERTICAL,
        adProductsAndFormats: filters.UNIFIED_ADPRODUCTSANDFORMATS,
        primaryContentCreatorTeam: filters.UNIFIED_PRIMARYCONTENTCREATORTEAM,
        brandName: filters.UNIFIED_BRANDNAME,
        meetingType: filters.MEETING_TYPE,
        meetingSubType: filters.MEETING_SUB_TYPE,
        opportunityCategory: filters.OPPORTUNITY_CATEGORY,
        opportunityStatus: filters.OPPORTUNITY_STATUS,
    }).reduce<[Record<string, string[]>, Record<string, string[]>]>(
        (acc, [key, filter]) => {
            const values = filter.map((i) => i.label);
            const [exact, fuzzy] = acc;

            const processedValues = processDeniedChars(values);
            if (processedValues !== values) {
                fuzzy[key] = processedValues;
            } else {
                exact[key] = values;
            }

            return [exact, fuzzy];
        },
        [{}, {}],
    );

    // Objectives aren't working while in exact search, so we need to force it into fuzzy search
    // This should be re-evaludated in the future
    fuzzyMappings.objectives = processDeniedChars(
        filters.UNIFIED_OBJECTIVES.map((i) => i.label),
    );

    // Only add type if subType.scope exists and is not "All"
    if (!ignoreScope) {
        exactMappings.type = filters.UNIFIED_TYPE.filter(
            (i) => !typesToIgnoreScopeFor.includes(i.id),
        ).map((i) => {
            // Map Marketing Content Industry Insights to the backend expected value - This is because two subtype filters share same id.
            return i.id === "Industry Insights - Marketing"
                ? "Industry Insights"
                : i.id;
        });
    }

    // Build our expressions to send to the Cerebro. This just puts it in the format:
    // (field1 == "value1" || field1 == "value2") && (field2 == "value3")
    const [exactExpression, fuzzyExpression] = [
        exactMappings,
        fuzzyMappings,
    ].map((mappings) =>
        Object.entries(mappings)
            .map(([name, values]) => {
                return values.map((v) => `${name} == "${v}"`).join(" || ");
            })
            .filter((o) => o)
            .map((e) => `(${e})`)
            .join(" && "),
    );

    // Perform a string replacement for standalone insight types in the search query
    // TODO: Remove once synonyms are released
    let modifiedQuery = searchQuery;
    Object.entries(InsightSearchReplacements).forEach(([key, value]) => {
        const regEx = new RegExp(key, "ig");
        modifiedQuery = modifiedQuery.replace(regEx, value);
    });

    return {
        searchQuery: modifiedQuery,
        exactExpression,
        fuzzyExpression,
        operator: "AND",
    };
};

export const convertToItemType = (
    options: IBaseFilterOptionModel,
): { label: string; value?: string } => {
    return {
        label: options.label,
        value: stormSafeId(options.id),
    };
};

/**
 * Certain storm components require a string ID, so this just converts any object it's given to a json representation
 * This is meant to be used in tandem with the parseStormSafeId function below to undo the type casting.
 * @param id The id we want to convert to a string
 * @returns The string representation of that id
 */
export const stormSafeId = (id: AllFilterOptionId): string => {
    if (typeof id === "string") return id;
    return JSON.stringify(id);
};

/**
 * This is the inverse of the stormSafeId function above. It will convert a string back to the original type
 * @param id The string representation of the id
 * @returns The original type of the id
 */
export const parseStormSafeId = (id: string): AllFilterOptionId => {
    try {
        return JSON.parse(id);
    } catch (e) {
        return id;
    }
};

export const convertAllCaps = (text: string | undefined): string => {
    return startCase(camelCase(text));
};

export const convertAdditionalInfoValues = (
    key: string,
    value: string | [],
): string => {
    if (Array.isArray(value)) {
        return value.join(", ");
    }
    if (key === "language") {
        return value
            .split(",")
            .map((item) => item.trim())
            .join(", ");
    }
    return capitalize(value);
};

const buildPackagingInsightDetails = (
    details: any,
    primaryContext: string | undefined,
    secondaryContext: string | undefined,
): CardDetail[] => {
    const entityId = details?.["entityId"];
    const entityName = details?.["entityName"];

    return [
        {
            label: "Insight type",
            value: insightTypeLabelToValue[InsightType.PACKAGING],
        },
        {
            label: "Brand",
            value: primaryContext,
        },
        {
            label: "BN",
            value: secondaryContext,
            lineClamp: 2,
        },
        ...(entityName ? [{ label: "Entity Name", value: entityName }] : []),
        {
            label: "Entity id",
            value: entityId,
        },
    ];
};

const buildStandaloneInsightDetails = (
    details: any,
    lastModifiedDate: string | undefined,
): CardDetail[] => {
    const metadata = details?.metadata;
    const startDate =
        metadata?.[InsightFamilyMetadataAttributes.START_DATE] ?? "";
    const endDate = metadata?.[InsightFamilyMetadataAttributes.END_DATE] ?? "";
    const brandType =
        metadata?.[InsightFamilyMetadataAttributes.BRAND_TYPE] ?? "";
    const brand = metadata?.[InsightFamilyMetadataAttributes.BRAND_NAME] ?? "";
    const refreshCadence =
        metadata?.[
            InsightFamilyMetadataAttributes.UPDATE_CADENCE
        ]?.toLowerCase() ?? "";
    const browseNode =
        metadata?.[InsightFamilyMetadataAttributes.BROWSE_NODE_NAME] ?? "";
    const familyName =
        metadata?.[InsightFamilyMetadataAttributes.FAMILY_NAME] ?? "";
    const objectives =
        metadata?.[InsightFamilyMetadataAttributes.OBJECTIVES] ?? "";

    return [
        {
            label: "Last updated on",
            value: formatUploadDate(lastModifiedDate),
            renderOn: [CardDetailsSource.CARD],
        },
        {
            label: "From",
            value: formatStartEndDate(startDate, endDate),
            renderOn: [CardDetailsSource.SIDEPANEL],
        },
        {
            label: "Last updated on",
            value: formatUploadDate(lastModifiedDate),
            tertiaryText: `Updated ${refreshCadence}`,
            renderOn: [CardDetailsSource.SIDEPANEL],
        },
        {
            label:
                brandType === InsightFamilyMetadataBrandType.ICM_BRAND
                    ? "Businessline"
                    : "Brand",
            value: brand,
        },
        {
            label: "BN",
            value: browseNode,
            lineClamp: 2,
        },
        {
            label: "Family name",
            value: formatFamilyName(familyName),
        },
        {
            label: "Objective",
            value: formatObjectives(objectives),
            renderOn: [CardDetailsSource.SIDEPANEL],
        },
    ];
};

export const buildSidePanelDetails = (
    content: Card,
    insightType: InsightType,
): CardDetail[] => {
    const { details, primaryContext, secondaryContext, lastModifiedDate } =
        content;

    if (insightType === InsightType.INSIGHT) {
        return buildStandaloneInsightDetails(details, lastModifiedDate);
    }

    if (insightType === InsightType.PACKAGING) {
        return buildPackagingInsightDetails(
            details,
            primaryContext,
            secondaryContext,
        );
    }

    return [];
};
